// pages/CaseDetailScreen.js

import React, { useState, useEffect } from 'react';
import { api } from '../api';
import toast from 'react-hot-toast';
import { ArrowLeft, Send, Edit, Lock } from 'lucide-react'; // --- ADDED: Edit icon
import '../styles/CasesScreen.css';
import UpdateCaseModal from '../components/support/UpdateCaseModal'; // --- ADDED: Import the new modal

const CaseDetailScreen = ({ caseId, setViewingCaseId, user }) => {
    const [caseDetails, setCaseDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // --- ADDED: State for the update modal (was already in your file) ---
    const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // This function is now responsible for getting ALL data, including comments
    const fetchAllData = async () => {
        if (!caseId) return;
        setIsLoading(true);
        try {
            const data = await api.getCaseById(String(caseId));
            setCaseDetails(data);
        } catch (error) {
            toast.error("Failed to load case details. It may not exist.");
            setViewingCaseId(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, [caseId]);

    const handleCommentSubmit = async (e) => {
        e.preventDefault();

        if (caseDetails.status === 'RESOLVED') {
            return toast.error("Cannot add comments to a resolved case.");
        }

        if (newComment.trim() === '') {
            return toast.error("Comment cannot be empty.");
        }
        setIsSubmitting(true);
        
        const formData = new FormData();
        formData.append('comment', newComment);

        try {
            await api.addCaseComment(caseId, formData);
            setNewComment('');
            toast.success('Comment added!');
            await fetchAllData(); 
        } catch (error) {
            console.error("Error submitting comment:", error);
            toast.error('Failed to add comment.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <div className="placeholder-text">Loading cases...</div>;
    }

    // Since comments are now part of caseDetails, we can safely render
    const comments = caseDetails?.comments || [];
    // --- ADDED: Variable to determine if the user has edit permissions ---
    const canEdit = user.role === 'SuperAdmin';
    const isCaseResolved = caseDetails.status === 'RESOLVED';

    // --- FIX: The return statement must have the opening parenthesis on the same line ---
    return (
        // --- ADDED: A React Fragment (<>) to wrap the modal and the main container ---
        <>
            {/* --- ADDED: Conditionally render the modal --- */}
            {isUpdateModalOpen && (
                <UpdateCaseModal
                    caseDetails={caseDetails}
                    user={user}
                    onClose={() => setUpdateModalOpen(false)}
                    onCaseUpdated={fetchAllData}
                />
            )}

            <div className="case-detail-container">
                <div className="page-header">
                    <button onClick={() => setViewingCaseId(null)} className="back-button">
                        <ArrowLeft size={18} />
                        <span>Back to All Cases</span>
                    </button>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h2>{caseDetails.subject}</h2>
                        {/* --- ADDED: A container for status and edit button for better layout --- */}
                        <div className="header-actions">
                            <span className={`status-badge status-${caseDetails.status.toLowerCase()}`}>{caseDetails.status}</span>
                            
                            {canEdit && (
                                <button onClick={() => setUpdateModalOpen(true)} className="edit-button" title="Edit Case">
                                    <Edit size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="card-body">
                        <p><strong>Case ID:</strong> #{caseDetails.id}</p>
                        <p><strong>Property:</strong> {caseDetails.property_name}</p>
                        <p><strong>Priority:</strong> {caseDetails.priority}</p>
                        <p><strong>Reported:</strong> {new Date(caseDetails.created_at).toLocaleString()}</p>
                        <hr />
                        <p>{caseDetails.description}</p>
                    </div>
                </div>

                <div className="comments-section card">
                    <h3>Comments ({comments.length})</h3>
                    <div className="comments-list">
                        {comments.length > 0 ? (
                            comments.map(c => (
                                <div key={c.id} className="comment">
                                    <p className="comment-author">
                                        <strong>{c.author}</strong>
                                        <span>{new Date(c.created_at).toLocaleString()}</span>
                                    </p>
                                    <p className="comment-text">{c.comment}</p>
                                </div>
                            ))
                        ) : (
                            <p className="placeholder-text">No comments yet.</p>
                        )}
                    </div>
                    <form className="comment-form" onSubmit={handleCommentSubmit}>
                        <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            disabled={isSubmitting}
                        />
                        <button  disabled={isSubmitting || isCaseResolved} className="button action-button">
                            {isCaseResolved ? <Lock size={16} /> : (isSubmitting ? '...' : <Send size={16} />)}
                        </button>
                    </form>
                </div>
            </div>
        </>
    );
};

export default CaseDetailScreen;